import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { getGetPrams } from '../../../utils/url'
import COLOR_LIST from '../../constants/taskColor'
import SearchAndSelect from './SearchAndSelect'
import { quoteSplit } from '../../../utils/string'
import { fetchTaskViewableLabelGroup } from '../../../states/containers/task/taskSlice'
import LoadingSquare from '../common/LoadingSquare'

// label_group のインクリメンタル検索で一度に取得する上限
const LABEL_GROUP_SEARCH_LIMIT = 1000

interface Props {
  callbackTeamIds?: (teamIds: number[]) => void
  callbackMemberIds?: (memberIds: number[]) => void
  callbackKeywords?: (keywords: string[]) => void
  callbackLabelMasterKeywords?: (labelMasterKeywords: string[]) => void
  callbackKey?: (key: string) => void
  callbackDirection?: (direction: string) => void
  callbackDisabled?: (disabled: boolean) => void
  callbackPageOffset?: (page: number) => void
  callbackLabelGroupIds?: (labelGroupIds: number[]) => void
  callbackMasterRelationLabelGroupIds?: (masterRelationLabelGroupList: number[]) => void
  callbackMasterRelationLabelGroupDisabled?: (disabled: boolean) => void
  callbackColorLabels?: (colorLabels: string[]) => void
  callbackCreatorMemberIds?: (memberIds: number[]) => void
  callbackViewerMemberIds?: (memberIds: number[]) => void
  callbackUserMemberIds?: (memberIds: number[]) => void
  mode?: 'member' | 'project'
  callbackConditionType?: (conditionType: string) => void
  callbackConditionKeywords?: (conditionKeywords: string[]) => void
  callbackLabelMasterIds?: (labelMasterIds: number[]) => void
  labelMasterList?: { id: number, name: string }[]
  memberList?: { id: number, name: string }[]
  teamList?: { id: number, name: string }[]
  labelGroupList?: { id: number, name: string }[]
  masterRelationLabelGroupList?: { id: number, name: string }[]
  isClear?: boolean
  // label_group をインクリメンタル検索（入力ごとに検索API）に切り替える。high_volume 用
  labelGroupIncrementalSearch?: boolean
  // インクリメンタル検索で管理下ユーザーが使用するプロジェクトのみに絞る（only_usable）
  labelGroupSearchMemberScope?: boolean
  // 選択中プロジェクトの id/name（インクリメンタル検索時の選択シード・名前解決用）
  selectedLabelGroupList?: { id: number, name: string }[]
}

// condition_type の値 → UI 表記
const CONDITION_TYPE_LABELS: { value: string, label: string }[] = [
  { value: "service", label: "アプリケーション" },
  { value: "keyword", label: "キーワード" },
  { value: "url", label: "URL" },
  { value: "file_path", label: "ファイルパス" },
  { value: "team_member", label: "チーム・ユーザー" },
]

interface GetParams {
  team_ids: string
  member_ids: string
  label_group_ids: string
  master_relation_label_group_ids: string
  only_unassigned: string
  keywords: string
  label_master_keywords: string
  key: string
  direction: string
  disabled: string
  colors: string
  creator_member_ids: string
  viewer_member_ids: string
  user_member_ids: string
  label_master_ids: string
  condition_type: string
  condition_keywords: string
}

const SearchModule: FC<Props> = (props) => {
  const {
    callbackTeamIds,
    callbackMemberIds,
    callbackKeywords,
    callbackLabelMasterKeywords,
    callbackMasterRelationLabelGroupIds,
    callbackMasterRelationLabelGroupDisabled,
    callbackKey,
    callbackDirection,
    callbackDisabled,
    callbackPageOffset,
    callbackLabelGroupIds,
    callbackColorLabels,
    callbackCreatorMemberIds,
    callbackViewerMemberIds,
    callbackUserMemberIds,
    mode,
    callbackConditionType,
    callbackConditionKeywords,
    callbackLabelMasterIds,
    labelMasterList,
    memberList,
    teamList,
    labelGroupList,
    masterRelationLabelGroupList,
    isClear,
    labelGroupIncrementalSearch,
    labelGroupSearchMemberScope,
    selectedLabelGroupList,
  } = props

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [isClickable, setIsClickable] = useState(true)

  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [labelGroupIds, setLabelGroupIds] = useState<number[]>([])
  // label_group インクリメンタル検索用（labelGroupIncrementalSearch=true のときのみ使用）
  const [selectLabelGroupObjects, setSelectLabelGroupObjects] = useState<{ id: number, name: string }[]>([])
  const [searchLabelGroupWord, setSearchLabelGroupWord] = useState<string>("")
  const [labelGroupComposition, setLabelGroupComposition] = useState<boolean>(false)
  const [isLabelGroupWordLoading, setIsLabelGroupWordLoading] = useState<boolean>(false)
  const [isLabelGroupSearched, setIsLabelGroupSearched] = useState<boolean>(false)
  const [searchedLabelGroupList, setSearchedLabelGroupList] = useState<{ id: number, name: string }[]>([])
  const [masterRelationLabelGroupIds, setMasterRelationLabelGroupIds] = useState<number[]>([])
  const [masterRelationLabelGroupDisabled, setMasterRelationLabelGroupDisabled] = useState<boolean>(false)
  const [colorLabels, setColorLabels] = useState<string[]>([])
  const [creatorMemberIds, setCreatorMemberIds] = useState<number[]>([])
  const [viewerMemberIds, setViewerMemberIds] = useState<number[]>([])
  const [userMemberIds, setUserMemberIds] = useState<number[]>([])
  const [searchKeyword, setSearchKeyword] = useState<string>("")
  const [labelMasterKeyword, setLabelMasterKeyword] = useState<string>("")
  const [searchDisabled, setSearchDisabled] = useState<boolean>(false)
  const [sortKey, setSortKey] = useState<string>("order_index")
  const [sortDirection, setSortDirection] = useState<string>("asc")
  const [labelMasterIds, setLabelMasterIds] = useState<number[]>([])
  const [conditionType, setConditionType] = useState<string>("")
  const [conditionKeyword, setConditionKeyword] = useState<string>("")

  const [initTeamIds, setInitTeamIds] = useState<number[]>([])
  const [initMemberIds, setInitMemberIds] = useState<number[]>([])
  const [initLabelGroupIds, setInitLabelGroupIds] = useState<number[]>([])
  const [initMasterRelationLabelGroupIds, setInitMasterRelationLabelGroupIds] = useState<number[]>([])
  const [initMasterRelationLabelGroupDisabled, setInitMasterRelationLabelGroupDisabled] = useState<boolean>(false)
  const [initColorLabels, setInitColorLabels] = useState<string[]>([])
  const [initCreatorMemberIds, setInitCreatorMemberIds] = useState<number[]>([])
  const [initViewerMemberIds, setInitViewerMemberIds] = useState<number[]>([])
  const [initUserMemberIds, setInitUserMemberIds] = useState<number[]>([])
  const [initSearchKeyword, setInitSearchKeyword] = useState<string>("")
  const [initLabelMasterKeyword, setInitLabelMasterKeyword] = useState<string>("")
  const [initSearchDisabled, setInitSearchDisabled] = useState<boolean>(false)
  const [initSortKey, setInitSortKey] = useState<string>("order_index")
  const [initSortDirection, setInitSortDirection] = useState<string>("asc")
  const [initLabelMasterIds, setInitLabelMasterIds] = useState<number[]>([])
  const [initConditionType, setInitConditionType] = useState<string>("")
  const [initConditionKeyword, setInitConditionKeyword] = useState<string>("")

  const isCanSubmit = useMemo(() => {
    if (
      searchKeyword === initSearchKeyword
      && labelMasterKeyword === initLabelMasterKeyword
      && teamIds.sort().join(',') === initTeamIds.sort().join(',')
      && memberIds.sort().join(',') === initMemberIds.sort().join(',')
      && labelGroupIds.sort().join(',') === initLabelGroupIds.sort().join(',')
      && masterRelationLabelGroupIds.sort().join(',') === initMasterRelationLabelGroupIds.sort().join(',')
      && masterRelationLabelGroupDisabled === initMasterRelationLabelGroupDisabled
      && colorLabels.sort().join(',') === initColorLabels.sort().join(',')
      && creatorMemberIds.sort().join(',') === initCreatorMemberIds.sort().join(',')
      && viewerMemberIds.sort().join(',') === initViewerMemberIds.sort().join(',')
      && userMemberIds.sort().join(',') === initUserMemberIds.sort().join(',')
      && searchDisabled === initSearchDisabled
      && sortKey === initSortKey
      && sortDirection === initSortDirection
      && labelMasterIds.sort().join(',') === initLabelMasterIds.sort().join(',')
      && conditionType === initConditionType
      && conditionKeyword === initConditionKeyword
    ) {
      return false
    }
    return true
  }, [
    searchKeyword,
    labelMasterKeyword,
    teamIds,
    memberIds,
    labelGroupIds,
    masterRelationLabelGroupIds,
    masterRelationLabelGroupDisabled,
    colorLabels,
    creatorMemberIds,
    viewerMemberIds,
    userMemberIds,
    searchDisabled,
    sortKey,
    sortDirection,
    labelMasterIds,
    conditionType,
    conditionKeyword,
  ])

  const clickTeam = (team_id: number) => {
    if (teamIds.includes(team_id)) {
      setTeamIds(teamIds.filter((id) => id !== team_id))
    } else {
      setTeamIds([...teamIds, team_id])
    }
  }

  const clickMember = (member_id: number) => {
    if (memberIds.includes(member_id)) {
      setMemberIds(memberIds.filter((id) => id !== member_id))
    } else {
      setMemberIds([...memberIds, member_id])
    }
  }

  const clickLabelGroup = (label_group_id: number) => {
    if (labelGroupIds.includes(label_group_id)) {
      setLabelGroupIds(labelGroupIds.filter((id) => id !== label_group_id))
    } else {
      setLabelGroupIds([...labelGroupIds, label_group_id])
    }
  }

  // インクリメンタル検索: 選択トグル（ids と name付きオブジェクトの両方を更新）
  const toggleLabelGroupIncremental = (labelGroup: { id: number, name: string }) => {
    if (labelGroupIds.includes(labelGroup.id)) {
      setLabelGroupIds(labelGroupIds.filter((id) => id !== labelGroup.id))
      setSelectLabelGroupObjects(selectLabelGroupObjects.filter((v) => v.id !== labelGroup.id))
    } else {
      setLabelGroupIds([...labelGroupIds, labelGroup.id])
      setSelectLabelGroupObjects([...selectLabelGroupObjects, { id: labelGroup.id, name: labelGroup.name }])
    }
  }

  const removeLabelGroupIncremental = (id: number) => {
    setLabelGroupIds(labelGroupIds.filter((v) => v !== id))
    setSelectLabelGroupObjects(selectLabelGroupObjects.filter((v) => v.id !== id))
  }

  const searchLabelGroup = () => {
    const words = searchLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    if (!words.length) {
      setSearchedLabelGroupList([])
      return
    }
    setIsLabelGroupWordLoading(true)
    dispatch(
      fetchTaskViewableLabelGroup(
        {
          keywords: words,
          limit: LABEL_GROUP_SEARCH_LIMIT,
          scope: labelGroupSearchMemberScope ? 'managed_members_usable' : 'viewable',
        },
        (result) => {
          setSearchedLabelGroupList(result.label_groups.map((v) => ({ id: v.id, name: v.name })))
          setIsLabelGroupWordLoading(false)
          setIsLabelGroupSearched(true)
        },
      ),
    )
  }

  const clickMasterRelationLabelGroup = (label_group_id: number) => {
    if (masterRelationLabelGroupIds.includes(label_group_id)) {
      setMasterRelationLabelGroupIds(masterRelationLabelGroupIds.filter((id) => id !== label_group_id))
    } else {
      setMasterRelationLabelGroupIds([...masterRelationLabelGroupIds, label_group_id])
    }
  }

  const clickColor = (color: string) => {
    if (colorLabels.includes(color)) {
      setColorLabels(colorLabels.filter((code) => code !== color))
    } else {
      setColorLabels([...colorLabels, color])
    }
  }

  const clickCreatorMember = (member_id: number) => {
    if (creatorMemberIds.includes(member_id)) {
      setCreatorMemberIds(creatorMemberIds.filter((id) => id !== member_id))
    } else {
      setCreatorMemberIds([...creatorMemberIds, member_id])
    }
  }

  const clickViewerMember = (member_id: number) => {
    if (viewerMemberIds.includes(member_id)) {
      setViewerMemberIds(viewerMemberIds.filter((id) => id !== member_id))
    } else {
      setViewerMemberIds([...viewerMemberIds, member_id])
    }
  }

  const clickUserMember = (member_id: number) => {
    if (userMemberIds.includes(member_id)) {
      setUserMemberIds(userMemberIds.filter((id) => id !== member_id))
    } else {
      setUserMemberIds([...userMemberIds, member_id])
    }
  }

  const clickLabelMaster = (label_master_id: number) => {
    if (labelMasterIds.includes(label_master_id)) {
      setLabelMasterIds(labelMasterIds.filter((id) => id !== label_master_id))
    } else {
      setLabelMasterIds([...labelMasterIds, label_master_id])
    }
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const cancelSearch = () => {
    setIsSearchDisplay(false)
  }

  const resetSearch = () => {
    setIsSearchDisplay(false)
    setTeamIds([])
    setMemberIds([])
    setLabelGroupIds([])
    setSelectLabelGroupObjects([])
    setSearchLabelGroupWord("")
    setSearchedLabelGroupList([])
    setMasterRelationLabelGroupIds([])
    setMasterRelationLabelGroupDisabled(false)
    setSearchKeyword("")
    setLabelMasterKeyword("")
    setSearchDisabled(false)
    setSortKey(login.feature_config?.high_volume ? "id" : "order_index")
    setSortDirection(login.feature_config?.high_volume ? "desc" : "asc")
    setColorLabels([])
    setCreatorMemberIds([])
    setViewerMemberIds([])
    setUserMemberIds([])
    setLabelMasterIds([])
    setConditionType("")
    setConditionKeyword("")
    if (callbackTeamIds) callbackTeamIds([])
    if (callbackMemberIds) callbackMemberIds([])
    if (callbackLabelGroupIds) callbackLabelGroupIds([])
    if (callbackMasterRelationLabelGroupIds) callbackMasterRelationLabelGroupIds([])
    if (callbackMasterRelationLabelGroupDisabled) callbackMasterRelationLabelGroupDisabled(false)
    if (callbackKeywords) callbackKeywords([])
    if (callbackLabelMasterKeywords) callbackLabelMasterKeywords([])
    if (callbackKey) callbackKey(login.feature_config?.high_volume ? "id" : "order_index")
    if (callbackDirection) callbackDirection(login.feature_config?.high_volume ? "desc" : "asc")
    if (callbackDisabled) callbackDisabled(false)
    if (callbackPageOffset) callbackPageOffset(1)
    if (callbackColorLabels) callbackColorLabels([])
    if (callbackCreatorMemberIds) callbackCreatorMemberIds([])
    if (callbackViewerMemberIds) callbackViewerMemberIds([])
    if (callbackUserMemberIds) callbackUserMemberIds([])
    if (callbackLabelMasterIds) callbackLabelMasterIds([])
    if (callbackConditionType) callbackConditionType("")
    if (callbackConditionKeywords) callbackConditionKeywords([])
  }

  const submitSearch = () => {
    const getParams = getGetPrams(location.search) as GetParams
    setIsSearchDisplay(false)
    const getParamTeamIds = getParams.team_ids ? getParams.team_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackTeamIds && (
        (getParamTeamIds.filter((value) => teamIds.indexOf(value) === -1).length)
        || (teamIds.filter((value) => getParamTeamIds.indexOf(value) === -1).length)
      )
    ) {
      callbackTeamIds(teamIds)
    }
    const getParamMemberIds = getParams.member_ids ? getParams.member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackMemberIds && (
        (getParamMemberIds.filter((value) => memberIds.indexOf(value) === -1).length)
        || (memberIds.filter((value) => getParamMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackMemberIds(memberIds)
    }
    const getParamLabelGroupIds = getParams.label_group_ids ? getParams.label_group_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackLabelGroupIds && (
        (getParamLabelGroupIds.filter((value) => labelGroupIds.indexOf(value) === -1).length)
        || (labelGroupIds.filter((value) => getParamLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelGroupIds(labelGroupIds)
    }
    const getParamMasterRelationLabelGroupIds = getParams.master_relation_label_group_ids
      ? getParams.master_relation_label_group_ids.split(',').map((v) => Number(v))
      : []
    if (
      callbackMasterRelationLabelGroupIds && (
        (getParamMasterRelationLabelGroupIds.filter((value) => masterRelationLabelGroupIds.indexOf(value) === -1).length)
        || (masterRelationLabelGroupIds.filter((value) => getParamMasterRelationLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      callbackMasterRelationLabelGroupIds(masterRelationLabelGroupIds)
    }
    if (callbackMasterRelationLabelGroupDisabled) {
      callbackMasterRelationLabelGroupDisabled(masterRelationLabelGroupDisabled)
    }
    const getParamKeywords = getParams.keywords ? getParams.keywords.split(',') : []
    const tmpKeywords = quoteSplit(searchKeyword).filter((v) => v)
    if (
      callbackKeywords && (
        (getParamKeywords.filter((value) => tmpKeywords.indexOf(value) === -1).length)
        || (tmpKeywords.filter((value) => getParamKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackKeywords(tmpKeywords)
    }
    const getParamLabelMasterKeywords = getParams.label_master_keywords ? getParams.label_master_keywords.split(',') : []
    const tmpLabelMasterKeywords = quoteSplit(labelMasterKeyword).filter((v) => v)
    if (
      callbackLabelMasterKeywords && (
        (getParamLabelMasterKeywords.filter((value) => tmpLabelMasterKeywords.indexOf(value) === -1).length)
        || (tmpLabelMasterKeywords.filter((value) => getParamLabelMasterKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelMasterKeywords(tmpLabelMasterKeywords)
    }
    if (callbackKey) {
      callbackKey(sortKey)
    }
    if (callbackDirection) {
      callbackDirection(sortDirection)
    }
    if (callbackDisabled) {
      callbackDisabled(searchDisabled)
    }
    if (callbackPageOffset) {
      callbackPageOffset(1)
    }
    if (callbackColorLabels) {
      callbackColorLabels(colorLabels)
    }
    const getParamCreatorMemberIds = getParams.creator_member_ids ? getParams.creator_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackCreatorMemberIds && (
        (getParamCreatorMemberIds.filter((value) => creatorMemberIds.indexOf(value) === -1).length)
        || (creatorMemberIds.filter((value) => getParamCreatorMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackCreatorMemberIds(creatorMemberIds)
    }
    const getParamViewerMemberIds = getParams.viewer_member_ids ? getParams.viewer_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackViewerMemberIds && (
        (getParamViewerMemberIds.filter((value) => viewerMemberIds.indexOf(value) === -1).length)
        || (viewerMemberIds.filter((value) => getParamViewerMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackViewerMemberIds(viewerMemberIds)
    }
    const getParamUserMemberIds = getParams.user_member_ids ? getParams.user_member_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackUserMemberIds && (
        (getParamUserMemberIds.filter((value) => userMemberIds.indexOf(value) === -1).length)
        || (userMemberIds.filter((value) => getParamUserMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackUserMemberIds(userMemberIds)
    }
    const getParamLabelMasterIds = getParams.label_master_ids ? getParams.label_master_ids.split(',').map((v) => Number(v)) : []
    if (
      callbackLabelMasterIds && (
        (getParamLabelMasterIds.filter((value) => labelMasterIds.indexOf(value) === -1).length)
        || (labelMasterIds.filter((value) => getParamLabelMasterIds.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelMasterIds(labelMasterIds)
    }
    if (callbackConditionType && (getParams.condition_type || "") !== conditionType) {
      callbackConditionType(conditionType)
    }
    const getParamConditionKeywords = getParams.condition_keywords ? getParams.condition_keywords.split(',') : []
    const tmpConditionKeywords = (conditionType && conditionType !== "team_member")
      ? quoteSplit(conditionKeyword).filter((v) => v)
      : []
    if (
      callbackConditionKeywords && (
        (getParamConditionKeywords.filter((value) => tmpConditionKeywords.indexOf(value) === -1).length)
        || (tmpConditionKeywords.filter((value) => getParamConditionKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackConditionKeywords(tmpConditionKeywords)
    }
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isSearchDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isSearchDisplay])

  useEffect(() => {
    if (!isSearchDisplay) return
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
      setInitTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else {
      setTeamIds([])
      setInitTeamIds([])
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
      setInitMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else {
      setMemberIds([])
      setInitMemberIds([])
    }
    if (getParams.label_group_ids) {
      setLabelGroupIds(getParams.label_group_ids.split(',').map((value) => Number(value)))
      setInitLabelGroupIds(getParams.label_group_ids.split(',').map((value) => Number(value)))
    } else {
      setLabelGroupIds([])
      setInitLabelGroupIds([])
    }
    if (getParams.master_relation_label_group_ids) {
      setMasterRelationLabelGroupIds(getParams.master_relation_label_group_ids.split(',').map((value) => Number(value)))
      setInitMasterRelationLabelGroupIds(getParams.master_relation_label_group_ids.split(',').map((value) => Number(value)))
    } else {
      setMasterRelationLabelGroupIds([])
      setInitMasterRelationLabelGroupIds([])
    }
    if (getParams.only_unassigned) {
      setMasterRelationLabelGroupDisabled(JSON.parse(getParams.only_unassigned.toLowerCase()))
      setInitMasterRelationLabelGroupDisabled(JSON.parse(getParams.only_unassigned.toLocaleLowerCase()))
    } else {
      setMasterRelationLabelGroupDisabled(false)
      setInitMasterRelationLabelGroupDisabled(false)
    }
    if (getParams.keywords) {
      const tmpKeywords = decodeURIComponent(getParams.keywords).split(',').map((v) => (v.includes(' ') ? `"${v}"` : v))
      setSearchKeyword(tmpKeywords.join(' '))
      setInitSearchKeyword(tmpKeywords.join(' '))
    } else {
      setSearchKeyword("")
      setInitSearchKeyword("")
    }
    if (getParams.label_master_keywords) {
      const tmpLabelMasterKeywords = decodeURIComponent(getParams.label_master_keywords).split(',').map((v) => (v.includes(' ') ? `"${v}"` : v))
      setLabelMasterKeyword(tmpLabelMasterKeywords.join(' '))
      setInitLabelMasterKeyword(tmpLabelMasterKeywords.join(' '))
    } else {
      setLabelMasterKeyword("")
      setInitLabelMasterKeyword("")
    }
    if (getParams.disabled) {
      setSearchDisabled(JSON.parse(getParams.disabled.toLowerCase()))
      setInitSearchDisabled(JSON.parse(getParams.disabled.toLowerCase()))
    } else {
      setSearchDisabled(false)
      setInitSearchDisabled(false)
    }
    if (getParams.key) {
      setSortKey(getParams.key)
      setInitSortKey(getParams.key)
    } else {
      setSortKey(login.feature_config?.high_volume ? "id" : "order_index")
      setInitSortKey(login.feature_config?.high_volume ? "id" : "order_index")
    }
    if (getParams.direction) {
      setSortDirection(getParams.direction)
      setInitSortDirection(getParams.direction)
    } else {
      setSortDirection(login.feature_config?.high_volume ? "desc" : "asc")
      setInitSortDirection(login.feature_config?.high_volume ? "desc" : "asc")
    }
    if (getParams.colors) {
      setColorLabels(getParams.colors.split(','))
      setInitColorLabels(getParams.colors.split(','))
    } else {
      setColorLabels([])
      setInitColorLabels([])
    }
    if (getParams.creator_member_ids) {
      setCreatorMemberIds(getParams.creator_member_ids.split(',').map((value) => Number(value)))
      setInitCreatorMemberIds(getParams.creator_member_ids.split(',').map((value) => Number(value)))
    } else {
      setCreatorMemberIds([])
      setInitCreatorMemberIds([])
    }
    if (getParams.viewer_member_ids) {
      setViewerMemberIds(getParams.viewer_member_ids.split(',').map((value) => Number(value)))
      setInitViewerMemberIds(getParams.viewer_member_ids.split(',').map((value) => Number(value)))
    } else {
      setViewerMemberIds([])
      setInitViewerMemberIds([])
    }
    if (getParams.user_member_ids) {
      setUserMemberIds(getParams.user_member_ids.split(',').map((value) => Number(value)))
      setInitUserMemberIds(getParams.user_member_ids.split(',').map((value) => Number(value)))
    } else {
      setUserMemberIds([])
      setInitUserMemberIds([])
    }
    if (getParams.label_master_ids) {
      setLabelMasterIds(getParams.label_master_ids.split(',').map((value) => Number(value)))
      setInitLabelMasterIds(getParams.label_master_ids.split(',').map((value) => Number(value)))
    } else {
      setLabelMasterIds([])
      setInitLabelMasterIds([])
    }
    if (getParams.condition_type) {
      setConditionType(getParams.condition_type)
      setInitConditionType(getParams.condition_type)
    } else {
      setConditionType("")
      setInitConditionType("")
    }
    if (getParams.condition_keywords) {
      const tmpConditionKeywords = decodeURIComponent(getParams.condition_keywords).split(',').map((v) => (v.includes(' ') ? `"${v}"` : v))
      setConditionKeyword(tmpConditionKeywords.join(' '))
      setInitConditionKeyword(tmpConditionKeywords.join(' '))
    } else {
      setConditionKeyword("")
      setInitConditionKeyword("")
    }
  }, [isSearchDisplay])

  useEffect(() => {
    if (!isSearchDisplay) return
    setSelectLabelGroupObjects(selectedLabelGroupList ?? [])
    setSearchLabelGroupWord("")
    setSearchedLabelGroupList([])
    setIsLabelGroupSearched(false)
  }, [isSearchDisplay])

  useEffect(() => {
    if (!labelGroupIncrementalSearch) return undefined
    setIsLabelGroupSearched(false)
    const timer = setTimeout(() => {
      if (!labelGroupComposition) {
        searchLabelGroup()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelGroupWord, labelGroupComposition])

  useEffect(() => {
    if (!isClear) return
    setTeamIds([])
    setMemberIds([])
    setLabelGroupIds([])
    setSelectLabelGroupObjects([])
    setSearchLabelGroupWord("")
    setSearchedLabelGroupList([])
    setMasterRelationLabelGroupIds([])
    setMasterRelationLabelGroupDisabled(false)
    setSearchKeyword("")
    setSearchDisabled(false)
    setLabelMasterKeyword("")
    setSortKey(login.feature_config?.high_volume ? "id" : "order_index")
    setSortDirection(login.feature_config?.high_volume ? "desc" : "asc")
    setColorLabels([])
    setCreatorMemberIds([])
    setViewerMemberIds([])
    setUserMemberIds([])
    setLabelMasterIds([])
    setConditionType("")
    setConditionKeyword("")

    setInitTeamIds([])
    setInitMemberIds([])
    setInitLabelGroupIds([])
    setInitMasterRelationLabelGroupIds([])
    setInitMasterRelationLabelGroupDisabled(false)
    setInitSearchKeyword("")
    setInitSearchDisabled(false)
    setInitLabelMasterKeyword("")
    setInitSortKey(login.feature_config?.high_volume ? "id" : "order_index")
    setInitSortDirection(login.feature_config?.high_volume ? "desc" : "asc")
    setInitColorLabels([])
    setInitCreatorMemberIds([])
    setInitViewerMemberIds([])
    setInitUserMemberIds([])
    setInitLabelMasterIds([])
    setInitConditionType("")
    setInitConditionKeyword("")
  }, [isClear])

  useEffect(() => {
    setIsClickable(!task.isLoading)
  }, [task.isLoading])

  return (
    <>
      <button
        className="search_button"
        type="button"
        onClick={() => openSearch()}
        disabled={!isClickable}
      >
        <FontAwesomeIcon icon={['fas', 'search']} />
        &nbsp;
        {
          callbackKey && callbackDirection ? (
            <>
              絞り込み・ソート
            </>
          ) : (
            <>
              絞り込み
            </>
          )
        }
      </button>
      <div className={`search_box ${isSearchDisplay || 'none'}`}>
        <div className="search_detail">
          {
            callbackKeywords ? (
              <div className="category">
                <div className="category_title">
                  プロジェクト名・プロジェクトコードで絞り込み
                  {
                    searchKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setSearchKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="検索したいプロジェクト名・プロジェクトコードを入力してください"
                  onChange={(event) => setSearchKeyword(event.target.value)}
                  value={searchKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackLabelMasterKeywords ? (
              <div className="category">
                <div className="category_title">
                  業務名で絞り込み
                  {
                    labelMasterKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setLabelMasterKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="検索したい業務名を入力してください"
                  onChange={(event) => setLabelMasterKeyword(event.target.value)}
                  value={labelMasterKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackTeamIds && mode !== 'project' ? (
              <SearchAndSelect
                categoryTitle="チームで絞り込み"
                searchPlaceholder="キーワードでチームを検索・選択してください"
                originalList={teamList || []}
                selectList={teamIds}
                callbackClick={(ele) => clickTeam(Number(ele))}
                callbackResetSelect={() => setTeamIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackMemberIds && mode !== 'project' ? (
              <SearchAndSelect
                categoryTitle="ユーザーで絞り込み"
                searchPlaceholder="キーワードでユーザーを検索・選択してください"
                originalList={memberList || []}
                selectList={memberIds}
                callbackClick={(ele) => clickMember(Number(ele))}
                callbackResetSelect={() => setMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackLabelGroupIds && !labelGroupIncrementalSearch && (
              <SearchAndSelect
                categoryTitle={mode === 'member' ? "仕分け先のプロジェクトで絞り込み" : "プロジェクト名・プロジェクトコードで絞り込み"}
                searchPlaceholder="キーワードでプロジェクト名を検索・選択してください"
                originalList={labelGroupList || []}
                selectList={labelGroupIds}
                callbackClick={(ele) => clickLabelGroup(Number(ele))}
                callbackResetSelect={() => setLabelGroupIds([])}
                keywordReset={isSearchDisplay}
              />
            )
          }
          {
            callbackLabelGroupIds && labelGroupIncrementalSearch && (
              <div className="category">
                <div className="category_title">
                  {mode === 'member' ? "仕分け先のプロジェクトで絞り込み" : "プロジェクト名・プロジェクトコードで絞り込み"}
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    placeholder="キーワードでプロジェクト名を検索・選択してください"
                    onChange={(event) => setSearchLabelGroupWord(event.target.value)}
                    onCompositionStart={() => setLabelGroupComposition(true)}
                    onCompositionEnd={() => setLabelGroupComposition(false)}
                    value={searchLabelGroupWord}
                    style={{ width: "100%" }}
                  />
                  {
                    !!searchLabelGroupWord.length && (
                      <button
                        type="button"
                        className="reset"
                        onClick={() => setSearchLabelGroupWord("")}
                      >
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                {
                  !!selectLabelGroupObjects.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        marginTop: "8px",
                        padding: "4px",
                      }}
                    >
                      <div style={{ padding: "4px", borderBottom: "dashed 1px #CCCCCC" }}>
                        選択済み
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          padding: "4px",
                        }}
                      >
                        {
                          selectLabelGroupObjects.map((v) => (
                            <div
                              key={`selected_label_group_${v.id}`}
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "20px",
                                padding: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                fontSize: "10px",
                              }}
                            >
                              {v.name}
                              <button
                                type="button"
                                onClick={() => removeLabelGroupIncremental(v.id)}
                                style={{ padding: "0" }}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  isLabelGroupWordLoading && (
                    <LoadingSquare>
                      <>
                        プロジェクトデータを取得しています…
                      </>
                    </LoadingSquare>
                  )
                }
                {
                  !isLabelGroupWordLoading
                  && !searchedLabelGroupList.filter((v) => !selectLabelGroupObjects.some((w) => w.id === v.id)).length && (
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#f5f7ff",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#999999",
                        marginTop: "8px",
                      }}
                    >
                      {!searchLabelGroupWord.length && "キーワードを入力して、プロジェクトを検索してください"}
                      {!!searchLabelGroupWord.length && (isLabelGroupSearched ? "キーワードに該当するプロジェクトがありませんでした" : "検索中…")}
                    </div>
                  )
                }
                {
                  !isLabelGroupWordLoading
                  && !!searchedLabelGroupList.filter((v) => !selectLabelGroupObjects.some((w) => w.id === v.id)).length && (
                    <>
                      {
                        searchedLabelGroupList.length === LABEL_GROUP_SEARCH_LIMIT && (
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              alignItems: "center",
                              gap: "4px",
                              border: "solid 1px #FF0000",
                              borderRadius: "4px",
                              backgroundColor: "#fff2f2",
                              color: "#FF0000",
                              padding: "8px",
                              marginBottom: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <div>
                              {`検索上位${LABEL_GROUP_SEARCH_LIMIT}件が表示されています`}
                            </div>
                            <div>
                              お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                            </div>
                          </div>
                        )
                      }
                      <div className="chip_list" style={{ marginTop: "8px" }}>
                        {
                          searchedLabelGroupList.filter((v) => !selectLabelGroupObjects.some((w) => w.id === v.id)).map((v) => (
                            <button
                              type="button"
                              className="chip"
                              key={`label_group_${v.id}`}
                              onClick={() => toggleLabelGroupIncremental(v)}
                            >
                              {v.name}
                            </button>
                          ))
                        }
                      </div>
                    </>
                  )
                }
              </div>
            )
          }
          {
            callbackLabelMasterIds ? (
              <SearchAndSelect
                categoryTitle="仕分け先の業務で絞り込み"
                searchPlaceholder="キーワードで業務を検索・選択してください"
                originalList={labelMasterList || []}
                selectList={labelMasterIds}
                callbackClick={(ele) => clickLabelMaster(Number(ele))}
                callbackResetSelect={() => setLabelMasterIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackConditionType ? (
              <div className="category">
                <div className="category_title">
                  仕分け条件
                  {
                    conditionType ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => {
                          setConditionType("")
                          setConditionKeyword("")
                          setTeamIds([])
                          setMemberIds([])
                        }}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <select
                  style={{
                    border: "solid 1px #ccc",
                    borderRadius: "4px",
                    padding: "8px",
                  }}
                  onChange={(event) => {
                    setConditionType(event.target.value)
                    // 条件種別を変えたら、それに紐づかない入力値はクリアする
                    setConditionKeyword("")
                    setTeamIds([])
                    setMemberIds([])
                  }}
                  value={conditionType}
                >
                  <option value="">指定なし</option>
                  {
                    CONDITION_TYPE_LABELS
                      .filter((item) => mode === 'project' || item.value !== 'team_member')
                      .map((item) => (
                        <option key={`condition_type_${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))
                  }
                </select>
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackConditionKeywords && conditionType && conditionType !== 'team_member' ? (
              <div className="category">
                <div className="category_title">
                  仕分け条件「
                  {CONDITION_TYPE_LABELS.find((v) => v.value === conditionType)?.label}
                  」の設定値
                  {
                    conditionKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setConditionKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="設定値を入力してください"
                  onChange={(event) => setConditionKeyword(event.target.value)}
                  value={conditionKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            // project モードで condition_type=team_member のとき、設定値の代わりに team/member を選択する
            callbackTeamIds && mode === 'project' && conditionType === 'team_member' ? (
              <SearchAndSelect
                categoryTitle="チームで絞り込み"
                searchPlaceholder="キーワードでチームを検索・選択してください"
                originalList={teamList || []}
                selectList={teamIds}
                callbackClick={(ele) => clickTeam(Number(ele))}
                callbackResetSelect={() => setTeamIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackMemberIds && mode === 'project' && conditionType === 'team_member' ? (
              <SearchAndSelect
                categoryTitle="ユーザーで絞り込み"
                searchPlaceholder="キーワードでユーザーを検索・選択してください"
                originalList={memberList || []}
                selectList={memberIds}
                callbackClick={(ele) => clickMember(Number(ele))}
                callbackResetSelect={() => setMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackMasterRelationLabelGroupIds ? (
              <SearchAndSelect
                categoryTitle="プロジェクト名・プロジェクトコードで絞り込み"
                searchPlaceholder="キーワードでプロジェクト名を検索・選択してください"
                originalList={masterRelationLabelGroupList || []}
                selectList={masterRelationLabelGroupIds}
                callbackClick={(ele) => clickMasterRelationLabelGroup(Number(ele))}
                callbackResetSelect={() => {
                  setMasterRelationLabelGroupIds([])
                  setMasterRelationLabelGroupDisabled(false)
                }}
                keywordReset={isSearchDisplay}
                disableOption={{
                  text: "プロジェクトに紐づいていない業務のみ表示する",
                  state: masterRelationLabelGroupDisabled,
                  callback: setMasterRelationLabelGroupDisabled,
                }}
              />
            ) : (
              <></>
            )
          }
          {
            callbackDisabled ? (
              <div className="category">
                <div className="category_title">
                  プロジェクトの状態
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchDisabled(!searchDisabled)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${!searchDisabled ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${!searchDisabled ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: !searchDisabled ? "#08984C" : "#666666",
                        }}
                      />
                      未完了
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSearchDisabled(!searchDisabled)}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${searchDisabled ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${searchDisabled ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: searchDisabled ? "#08984C" : "#666666",
                        }}
                      />
                      完了
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackKey && callbackDirection ? (
              <div className="category">
                <div className="category_title">
                  ソート
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <select
                    style={{
                      border: "solid 1px #ccc",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                    onChange={(event) => setSortKey(event.target.value)}
                    value={sortKey}
                  >
                    <option value="id">
                      プロジェクト作成日
                    </option>
                    {
                      !login.feature_config?.high_volume && (
                        <option value="order_index">
                          カスタム設定
                        </option>
                      )
                    }
                    <option value="scheduled_start_date">
                      プロジェクト開始日
                    </option>
                    <option value="scheduled_end_date">
                      プロジェクト終了予定日
                    </option>
                    <option value="disabled_at">
                      プロジェクト完了日
                    </option>
                    <option value="name">
                      プロジェクト名
                    </option>
                    <option value="code">
                      プロジェクトコード
                    </option>
                  </select>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSortDirection("asc")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${sortDirection === "asc" ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${sortDirection === "asc" ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: sortDirection === "asc" ? "#08984C" : "#666666",
                        }}
                      />
                      昇順
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSortDirection("desc")}
                      role="none"
                    >
                      <FontAwesomeIcon
                        icon={['far', `${sortDirection === "desc" ? 'dot-circle' : 'circle'}` as IconName]}
                        className={`icon ${sortDirection === "desc" ? "checked" : "no_check"}`}
                        style={{
                          fontSize: "18px",
                          color: sortDirection === "desc" ? "#08984C" : "#666666",
                        }}
                      />
                      降順
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackCreatorMemberIds ? (
              <SearchAndSelect
                categoryTitle="作成者で絞り込み"
                searchPlaceholder="キーワードで作成者を検索・選択してください"
                originalList={memberList || []}
                selectList={creatorMemberIds}
                callbackClick={(ele) => clickCreatorMember(Number(ele))}
                callbackResetSelect={() => setCreatorMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackViewerMemberIds ? (
              <SearchAndSelect
                categoryTitle="プロジェクトレポート表示対象ユーザーで絞り込み"
                searchPlaceholder="キーワードでプロジェクトレポート表示対象ユーザーを検索・選択してください"
                originalList={memberList || []}
                selectList={viewerMemberIds}
                callbackClick={(ele) => clickViewerMember(Number(ele))}
                callbackResetSelect={() => setViewerMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackUserMemberIds ? (
              <SearchAndSelect
                categoryTitle="プロジェクト業務仕分け対象ユーザーで絞り込み"
                searchPlaceholder="キーワードでプロジェクト業務仕分け対象ユーザーを検索・選択してください"
                originalList={memberList || []}
                selectList={userMemberIds}
                callbackClick={(ele) => clickUserMember(Number(ele))}
                callbackResetSelect={() => setUserMemberIds([])}
                keywordReset={isSearchDisplay}
              />
            ) : (
              <></>
            )
          }
          {
            callbackColorLabels ? (
              <div className="category">
                <div className="category_title">
                  カラーで絞り込み
                  {
                    colorLabels.length ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setColorLabels([])}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="chip_list">
                  {
                    COLOR_LIST.map((colorCategory) => (
                      <React.Fragment key={`color_category_${colorCategory.name}`}>
                        {
                          colorCategory.values.map((color) => (
                            <button
                              type="button"
                              className={`chip ${colorLabels.includes(color.label) ? "checked" : ""}`}
                              key={`color_${color.text}`}
                              onClick={() => clickColor(color.label)}
                            >
                              <div
                                style={{
                                  backgroundColor: color.color,
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                }}
                              />
                              {color.text}
                            </button>
                          ))
                        }
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
            ) : (
              <></>
            )
          }
        </div>
        <div className="button_area" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "16px", gap: "40px" }}>
          <button
            type="button"
            className="button cancel"
            onClick={resetSearch}
          >
            絞り込み条件をリセット
          </button>
          <button
            type="button"
            className="button"
            onClick={submitSearch}
            disabled={!isCanSubmit}
          >
            絞り込む
          </button>
        </div>
      </div>
      <div className={`close_screen gray ${isSearchDisplay || 'none'}`} role="presentation" onClick={cancelSearch} />
    </>
  )
}

SearchModule.defaultProps = {
  callbackTeamIds: undefined,
  callbackMemberIds: undefined,
  callbackKeywords: undefined,
  callbackLabelMasterKeywords: undefined,
  callbackKey: undefined,
  callbackDirection: undefined,
  callbackDisabled: undefined,
  callbackPageOffset: undefined,
  callbackLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupIds: undefined,
  callbackMasterRelationLabelGroupDisabled: undefined,
  callbackColorLabels: undefined,
  callbackCreatorMemberIds: undefined,
  callbackViewerMemberIds: undefined,
  callbackUserMemberIds: undefined,
  mode: undefined,
  callbackConditionType: undefined,
  callbackConditionKeywords: undefined,
  callbackLabelMasterIds: undefined,
  labelMasterList: undefined,
  memberList: undefined,
  teamList: undefined,
  labelGroupList: undefined,
  masterRelationLabelGroupList: undefined,
  isClear: false,
  labelGroupIncrementalSearch: false,
  labelGroupSearchMemberScope: false,
  selectedLabelGroupList: undefined,
}

export default SearchModule
